import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { SystemNotificationDto } from 'app/api/models';
import { SystemNotificationService } from 'app/api/services';
import { JwtAuthService } from 'app/api/services/cookie-jwt-auth/jwt-auth.service';
import { SignalRService } from 'app/core/services/signalr.service';
import { TimeUtil } from 'app/core/utils/time-utils';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    imports: [MatButtonModule, MatIconModule, MatTooltipModule, NgClass, NgTemplateOutlet, RouterLink, DatePipe]
})
export class NotificationsComponent implements OnInit, OnDestroy
{
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: SystemNotificationDto[] = [];
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private notifService: SystemNotificationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private jwtAuth: JwtAuthService,
        private signalRService: SignalRService,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this.loadNotifications();
        this.subscribeToRealTimeNotifications();
    }

    /**
     * Subscribe to real-time notifications via SignalR
     */
    private subscribeToRealTimeNotifications(): void
    {
        // Start SignalR connection
        this.signalRService.startConnection();

        // Listen for new system notifications
        this.signalRService.notification$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notification: SystemNotificationDto) => {
                // Add new notification to the top of the list
                this.notifications.unshift(notification);
                this._calculateUnreadCount();
                this._changeDetectorRef.markForCheck();
            });
    }

    loadNotifications() {
        this.notifService.apiSystemNotificationForUserGet$Json$Response({userId: this.jwtAuth.getUser().id}).subscribe(res => {
            if(!res?.body?.data) return;

            this.notifications = res.body.data;

            this._calculateUnreadCount();

            this._changeDetectorRef.markForCheck();
        });
    }

    public getAdjustedDate = TimeUtil.getTimezoneAdjustedDate;

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if ( this._overlayRef )
        {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void
    {
        // Return if the notifications panel or its origin is not defined
        if ( !this._notificationsPanel || !this._notificationsOrigin )
        {
            return;
        }

        // Create the overlay if it doesn't exist
        if ( !this._overlayRef )
        {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void
    {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void
    {
        for(var notif of this.notifications)
            notif.read = true;

        this.notifService.apiSystemNotificationMarkAllReadPut$Json$Response({userId: this.jwtAuth.getUser().id}).subscribe();
        
        this._calculateUnreadCount();
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: SystemNotificationDto): void
    {
        notification.read = !notification.read;

        this.notifService.apiSystemNotificationUpdateNotificationPut$Json$Response({body: notification}).subscribe();

        this._calculateUnreadCount();
    }

    /**
     * Delete the given notification
     */
    dismiss(notification: SystemNotificationDto): void
    {
        // Mark as dismissed in database
        notification.dismissed = true;
        this.notifService.apiSystemNotificationUpdateNotificationPut$Json$Response({body: notification}).subscribe();

        // Remove from local array
        this.notifications = this.notifications.filter(n => n.systemNotificationId !== notification.systemNotificationId);

        // Recalculate unread count and trigger change detection
        this._calculateUnreadCount();
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void
    {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX : 'start',
                        originY : 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX : 'start',
                        originY : 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX : 'end',
                        originY : 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX : 'end',
                        originY : 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() =>
        {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void
    {
        let count = 0;

        if ( this.notifications && this.notifications.length )
        {
            count = this.notifications.filter(notification => !notification.read).length;
        }

        this.unreadCount = count;

        this._changeDetectorRef.markForCheck();
    }
}
